<template>
  <div v-if="auditHistory.length">
    <span class="font-italic ml-3 font-weight-thin"
      >Last updated by {{ formatUsername(auditHistory[0].username) }} on
      {{ formatUTCtoPST(auditHistory[0].created) }}</span
    >
    <v-btn @click.stop="setDialog(true)" class="ml-3 mb-1" color="primary" data-cy="serviceAreaHistoryDialog" text small
      >View History of Changes</v-btn
    >
    <v-dialog v-model="dialog" max-width="80%">
      <v-card>
        <v-card-title class="headline font-weight-thin">Change History: Excluded Application List</v-card-title>
        <v-card-text class="mt-3">
          Below you will find changes to the list of applications that DO NOT impact your organization, a.k.a. the
          "Excluded Application List".
        </v-card-text>
        <v-card-text> </v-card-text>
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="auditHistory"
            :items-per-page="5"
            class="elevation-0"
            data-cy="appHistory"
            data-test="appHistory"
            dense
            item-key="logId"
          >
            <template v-slot:item="{ item }">
              <tr>
                <td data-cy="created">{{ formatUTCtoPST(item.created) }}</td>
                <td data-cy="username">{{ formatUsername(item.username) }}</td>
                <td data-cy="oldValue">
                  <li v-for="(oldApp, index) in formatOldValue(item.content)" :key="index">
                    <span
                      :class="
                        checkDiff(oldApp, item.content) === 'removed' ? 'red--text font-weight-bold text--darken-2' : ''
                      "
                      >{{ oldApp.label }}
                      <span
                        v-if="checkDiff(oldApp, item.content) === 'removed'"
                        class="font-italic red--text text--darken-2 caption"
                        >(REMOVED)</span
                      >
                    </span>
                  </li>
                </td>
                <td data-cy="newValue" class="py-3">
                  <span v-if="isInitialAcknowledgement(item.content)">initial acknowledgement</span>
                  <li v-for="(newApp, index) in formatNewValue(item.content)" :key="index">
                    <span
                      :class="
                        checkDiff(newApp, item.content) === 'added'
                          ? 'primary--text font-weight-bold text--darken-1'
                          : ''
                      "
                      >{{ newApp.label }}
                      <span v-if="checkDiff(newApp, item.content) === 'added'" class="font-italic primary--text caption"
                        >(ADDED)</span
                      ></span
                    >
                  </li>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="setDialog(false)" class="mr-2" outlined>Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatUsername, formatUTCtoPST } from "@/lib/formatters";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  computed: {
    ...mapGetters({
      userMap: "user/getUserMap",
    }),
    ...mapState({
      userList: state => state.user.userList,
    }),
  },
  async created() {
    try {
      await Promise.all([this.fetchUserList()]);
    } catch (e) {
      console.log(e);
      this.$notifyError("Error fetching user list");
    }
  },
  data() {
    return {
      dialog: false,
      headers: [
        { text: "Date Updated", value: "created" },
        { text: "Edited By", value: "username" },
        { text: "Previous Excluded Application List", value: "content" },
        { text: "Updated Excluded Application List", value: "content" },
      ],
      formatUTCtoPST,
    };
  },
  methods: {
    checkDiff(val, itemContent) {
      if (itemContent[0].oldValue === null && itemContent[0].newValue.length > 0) {
        return "added";
      }
      const oldValues = itemContent[0].oldValue;
      const newValues = itemContent[0].newValue;

      if (oldValues.includes(val.id) && newValues.includes(val.id)) {
        return "unchanged";
      } else if (oldValues.includes(val.id) && !newValues.includes(val.id)) {
        return "removed";
      } else if (!oldValues.includes(val.id) && newValues.includes(val.id)) {
        return "added";
      }
    },
    isInitialAcknowledgement(itemContent) {
      // Before organization makes any changes to the excluded application list, the initial acknowledgement message is explained.
      // When they click this, the value is set from null to an empty array in the database. Thereafter, it will never again be null.
      return itemContent[0].oldValue === null && itemContent[0].newValue.length === 0;
    },
    formatUsername(username) {
      return formatUsername(username, this.userMap);
    },
    formatOldValue(content) {
      const oldValueID = content[0].oldValue;

      if (oldValueID == null) {
        return "";
      }
      let oldValues = [];
      this.adminDropdownList.forEach(dropdown => {
        if (oldValueID.includes(dropdown.id)) {
          oldValues.push(dropdown);
        }
      });

      return oldValues;
    },
    formatNewValue(content) {
      const newValueID = content[0].newValue;
      let newValues = [];
      this.adminDropdownList.forEach(dropdown => {
        if (newValueID.includes(dropdown.id)) {
          newValues.push(dropdown);
        }
      });
      return newValues;
    },
    formatRemovedValues(content) {
      const removedValues = this.getRemovedValues(content);
      if (removedValues === null) {
        return "";
      }
      let removedValuesList = [];
      this.adminDropdownList.forEach(dropdown => {
        if (removedValues.includes(dropdown.id)) {
          removedValuesList.push(dropdown.label);
        }
      });
      return removedValuesList;
    },
    getRemovedValues(itemContent) {
      const oldValues = itemContent[0].oldValue;
      if (oldValues === null) {
        return null;
      }
      const newValues = itemContent[0].newValue;
      const removedValues = oldValues.filter(val => !newValues.includes(val));

      return removedValues;
    },
    async setDialog(val) {
      this.dialog = val;
    },
    ...mapActions({
      fetchUserList: "user/load",
    }),
  },
  props: {
    adminDropdownList: { required: true, type: Array },
    auditHistory: { required: true, type: Array },
  },
};
</script>
